import { X } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/cn';

/**
 * docs/02-DESIGN-SYSTEM.md §5 — the sheet behind the seven student/entrant
 * popups and the club detail.
 *
 * Controlled: the caller owns `open`. §5 also requires that opening writes
 * `?modal=<key>` to the URL and closing removes it, which is routing rather
 * than presentation — the pages wire that with `useSearchParams` in Phase 3,
 * and the deep-link journey is E2E case 4 in docs/06-FRONTEND-SPEC.md §10.
 * Keeping the router out of here is what lets the same sheet be reused by the
 * admin dashboard, which has no such URL contract.
 *
 * Hand-rolled rather than built on `<dialog>`: the native element brings its
 * own focus and Esc handling but its `::backdrop` cannot be blurred
 * consistently across the browsers this audience uses, and its behaviour is
 * still uneven enough that the §5 checklist would need re-implementing on top
 * anyway.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Rendered as the sheet's heading and used as its accessible name. */
  title: ReactNode;
  /** Optional lead paragraph — `body-lg` semibold (§5). */
  lead?: ReactNode;
  /** Accessible name for the close control, e.g. "დახურვა". */
  closeLabel: string;
  /**
   * When false, Escape and a backdrop click do nothing and no close control is
   * rendered — the sheet can only be left through its own actions.
   *
   * For the one case where dismissing is not a choice the person has: the
   * admin's re-auth modal (docs/05-ADMIN-DASHBOARD.md §6.7) sits over a
   * dashboard whose every request is failing, and closing it would leave a
   * screen that looks broken rather than expired. Default true — a modal a
   * reader cannot escape is otherwise a trap, and §5's checklist assumes they
   * can.
   */
  dismissible?: boolean;
  /** Optional footer, typically a single primary CTA. */
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
}

export function Modal({
  open,
  onClose,
  title,
  lead,
  closeLabel,
  footer,
  children,
  className,
  dismissible = true,
}: ModalProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();

  const focusableElements = useCallback(
    () => Array.from(sheetRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
    [],
  );

  // Remember the trigger and restore focus to it on close (§5). Without this,
  // closing a modal drops a keyboard user back at the top of the document.
  useEffect(() => {
    if (!open) {
      return;
    }

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    // Focus the sheet itself rather than its first control: the title should
    // be the first thing announced, not the close button.
    sheetRef.current?.focus();

    return () => {
      previouslyFocused.current?.focus();
    };
  }, [open]);

  // Lock body scroll, compensating for the scrollbar so the page behind does
  // not visibly jump sideways as the sheet opens (§6.1 CLS budget).
  useEffect(() => {
    if (!open) {
      return;
    }

    const { body, documentElement } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingInlineEnd;
    const scrollbar = window.innerWidth - documentElement.clientWidth;

    body.style.overflow = 'hidden';

    if (scrollbar > 0) {
      body.style.paddingInlineEnd = `${scrollbar}px`;
    }

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingInlineEnd = previousPadding;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        // Still swallowed when the sheet is not dismissible: letting it through
        // would close whatever is behind instead.
        event.stopPropagation();

        if (dismissible) {
          onClose();
        }

        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      // Focus trap (§5). Tab from the last control wraps to the first and
      // Shift+Tab from the first wraps to the last, so focus never escapes
      // into the page behind.
      const elements = focusableElements();

      if (elements.length === 0) {
        event.preventDefault();

        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];
      const active = document.activeElement;

      if (!first || !last) {
        return;
      }

      if (event.shiftKey && (active === first || active === sheetRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, onClose, focusableElements, dismissible]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  /*
   * Backdrop click closes (§5). Only a press that both starts and ends on the
   * backdrop counts — otherwise a text selection dragged out of the sheet
   * would close it mid-gesture.
   *
   * jsx-a11y asks for keyboard parity on any element carrying a mouse handler.
   * That parity exists, just not on this element: Escape closes the sheet and
   * the close button is the first control in the focus trap. Making the
   * backdrop focusable would add a second, unlabelled tab stop to every modal
   * — worse for keyboard users, not better.
   */
  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-50 flex items-center justify-center sm:p-6"
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[rgba(26,24,20,0.55)] backdrop-blur-[4px]"
      />

      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'relative flex max-h-full w-full flex-col overflow-hidden bg-surface shadow-lg outline-none',
          // Mobile: a full-screen sheet with a sticky header (§5).
          'h-full sm:h-auto sm:max-h-[85vh] sm:w-[min(92vw,1100px)] sm:rounded-xl',
          className,
        )}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 bg-surface px-6 pt-6 sm:static sm:px-[clamp(24px,5vw,64px)] sm:pt-[clamp(24px,5vw,64px)]">
          <h2 id={titleId} className="text-h2 text-primary">
            {title}
          </h2>

          {dismissible ? (
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              // §5 — 32px minimum hit area.
              className="-me-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-secondary transition-colors duration-fast hover:bg-surface-alt hover:text-primary"
            >
              <X aria-hidden="true" size={24} />
            </button>
          ) : null}
        </div>

        {/*
          The body scrolls once the content passes 85vh, so it has to be
          keyboard-reachable: focus sits on the sheet, which is not itself
          scrollable, so without this a keyboard user cannot page through a
          long modal at all. axe's `scrollable-region-focusable` fails at
          `serious`, and the Scholarships and Preparation Centre modals are
          both long enough to hit it.
        */}
        <div
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          className="flex-1 overflow-y-auto px-6 pb-6 sm:px-[clamp(24px,5vw,64px)] sm:pb-[clamp(24px,5vw,64px)]"
        >
          {/* §5 — space-4 below the title when a lead follows, space-10 otherwise. */}
          {lead ? (
            <p className="mb-8 mt-4 max-w-prose text-body-lg font-semibold text-primary">{lead}</p>
          ) : (
            <div className="h-10" />
          )}

          {/* §5 — body at the `body` token, 78ch, with rich-text support. */}
          <div className="rich-text max-w-prose text-body text-primary">{children}</div>

          {footer ? <div className="mt-10">{footer}</div> : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
